/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import { attemptRequest } from '../utilities/redux-request-deduplication';
import mcLib from './mc-lib';
import output from '../utilities/output';

// TODO: These are the methods which were formerly used with the mc-os project in the lightning Global Nav
// They can be removed _or_ they could be reactivated so that the homepage app can adaptively run in either
// the existing Marketing Cloud app or the new Global Nav.
//
// function fuelApiGet (url, data) {
// 	return new Promise((resolve, reject) => {
// 		mcLib.fuelApiGet(url, data, (result) => {
// 			if (!result.err) {
// 				resolve(result);
// 			} else {
// 				reject(result.msg);
// 			}
// 		});
// 	});
// }
//
// function fuelApiPost (url, data) {
// 	return new Promise((resolve, reject) => {
// 		mcLib.fuelApiPost(url, data, (result) => {
// 			if (!result.err) {
// 				resolve(result);
// 			} else {
// 				reject(result.msg);
// 			}
// 		});
// 	});
// }
//
// function legacyApiGet (url, data) {
// 	return new Promise((resolve, reject) => {
// 		mcLib.legacyApiGet(url, data, (result) => {
// 			if (!result.err) {
// 				resolve(result);
// 			} else {
// 				reject(result.msg);
// 			}
// 		});
// 	});
// }

function addSuppressErrorParameter(url) {
  if (url && url.match('\\?')) {
    return `${url}&suppressError`;
  }

  return `${url}?suppressError`;
}

function fuelAjaxGet(url, data) {
  return new Promise((resolve, reject) => {
    mcLib.fuelAjaxGet(addSuppressErrorParameter(url), data, (result) => {
      if (result.response && !result.errorMsg) {
        resolve(result.response);
      } else {
        reject(new Error(result.errorMsg));
      }
    });
  });
}

function fuelAjaxPost(url, data) {
  return new Promise((resolve, reject) => {
    mcLib.fuelAjaxPost(addSuppressErrorParameter(url), data, (result) => {
      if (result.response && !result.errorMsg) {
        resolve(result.response);
      } else {
        reject(new Error(result.errorMsg));
      }
    });
  });
}

function legacyAjaxGet(url, data) {
  return new Promise((resolve, reject) => {
    mcLib.legacyAjaxGet(addSuppressErrorParameter(url), data, (result) => {
      if (result.response && !result.errorMsg) {
        resolve(result.response);
      } else {
        reject(new Error(result.errorMsg));
      }
    });
  });
}

function getProvisionedApplications() {
  return new Promise((resolve, reject) => {
    mcLib.getProvisionedApplications((result) => {
      if (result.apps && !result.errorMsg) {
        resolve(result.apps);
      } else {
        reject(new Error(result.errorMsg));
      }
    });
  });
}

function getUserInfo() {
  return new Promise((resolve, reject) => {
    mcLib.getUserInfo((userInfo) => {
      if (userInfo) {
        resolve(userInfo);
      } else {
        reject(new Error('Unable to get user info'));
      }
    });
  });
}

function hasAccess(permissionSetName) {
  return new Promise((resolve, reject) => {
    mcLib.hasAccess(permissionSetName, 'access', (result) => {
      if (typeof result === 'boolean') {
        resolve(result);
      } else if (result && result.errorMsg) {
        reject(new Error(result.errorMsg));
      } else {
        reject(new Error(`Unable to determine access to ${permissionSetName}`));
      }
    });
  });
}

function hasPermission(permissionSetName, permission) {
  return new Promise((resolve, reject) => {
    mcLib.hasAccess(permissionSetName, permission, (result) => {
      if (typeof result === 'boolean') {
        resolve(result);
      } else if (result && result.errorMsg) {
        reject(new Error(result.errorMsg));
      } else {
        reject(new Error(`Unable to determine access to ${permissionSetName}`));
      }
    });
  });
}

function getBusinessRule(brName) {
  return new Promise((resolve, reject) => {
    mcLib.getBusinessRule(brName, (result) => {
      if (result.businessRule) {
        resolve(result.businessRule);
      } else {
        reject(new Error(result.err));
      }
    });
  });
}

// TODO: we need more than just access permissions. But we can figure that out later

function createRequestAttempt(requestMethod, actionName, requestId, payload = {}) {
  return function (dispatch) {
    attemptRequest(
      requestId,
      {
        begin: () => ({
          type: actionName,
          payload,
        }),
        success: (response) => ({
          type: actionName,
          payload: {
            ...payload,
            response,
          },
        }),
        failure: (error) => {
          output.error(error);

          return {
            type: actionName,
            error: error instanceof Error ? error.message : error,
            payload,
          };
        },
      },
      requestMethod,
      dispatch
    );
  };
}

export function createMcGetAction(actionName, url, options = {}) {
  const { requestId = url, payload, data } = options;

  return createRequestAttempt(() => fuelAjaxGet(url, data), actionName, requestId, payload);
}

export function createMcPostAction(actionName, url, options = {}) {
  const { requestId = url, payload, data } = options;

  return createRequestAttempt(() => fuelAjaxPost(url, data), actionName, requestId, payload);
}

export function createMcLegacyGetAction(actionName, url, options = {}) {
  const { requestId = url, payload, data } = options;

  return createRequestAttempt(() => legacyAjaxGet(url, data), actionName, requestId, payload);
}

export function createMcProvisionedApplicationsAction(actionName, options = {}) {
  const { requestId = 'McProvisionedApplications', payload } = options;

  return createRequestAttempt(() => getProvisionedApplications(), actionName, requestId, payload);
}

export function createMcUserInfoAction(actionName, options = {}) {
  const { requestId = 'McUserInfo', payload } = options;

  return createRequestAttempt(() => getUserInfo(), actionName, requestId, payload);
}

export function createMcHasAccessAction(actionName, permissionSetName, options = {}) {
  const { requestId = `McHasAccess:${permissionSetName}`, payload } = options;

  return createRequestAttempt(() => hasAccess(permissionSetName), actionName, requestId, payload);
}

export function createMcHasPermissionAction(actionName, permissionSetName, permissionName, options = {}) {
  const { requestId = `McHasPermission:${permissionSetName}:${permissionName}`, payload } = options;

  return createRequestAttempt(() => hasPermission(permissionSetName, permissionName), actionName, requestId, payload);
}

export function createMcGetBusinessRule(actionName, brName, options = {}) {
  const { requestId = `McGetBusinessRule:${actionName}:${brName}`, payload } = options;

  return createRequestAttempt(() => getBusinessRule(brName), actionName, requestId, payload);
}
